import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  AutoComplete,
  Button,
  ConfigProvider,
  Form,
  Input,
  Modal,
  Select,
  Switch,
  Tabs,
  Typography,
  message,
  theme,
} from "antd";
import { DeleteOutlined, PlusOutlined, SearchOutlined, CaretDownOutlined, CaretUpOutlined, InfoCircleOutlined } from "@ant-design/icons";
import EditIcon from "../../../ui/EditIcon";
import CustomLayout from "../../../Layout/CustomLayout";
import SideMenu from "../../../Layout/CustomLayout/SideMenu";
import styles from "./FacilityIpWhitelisting.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { getCookie } from "../../../../helpers/localStorage";
import { FacilityID, ORGANISATONID } from "../../../../constants/defaultKeys";
import { UserContext } from "../../../../context/UserContext";
import { getPractitionerSearchApi } from "../../../../services/receptionist.services";
import { capitalizeFirstLetter, debounce } from "../../../../helpers/utility";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import { updateUrlSearchParams } from "../../../../helpers/urlFilters";
import {
  addIpWhitelistingFacilityIpApi,
  updateIpWhitelistingFacilityIpApi,
  deleteIpWhitelistingFacilityIpApi,
  getIpWhitelistingAccessLogsApi,
  getIpWhitelistingBypassUsersApi,
  getIpWhitelistingConfigsApi,
  getIpWhitelistingFacilityIpsApi,
  createIpWhitelistingBypassUserApi,
  deleteIpWhitelistingBypassUserApi,
  updateIpWhitelistingBypassUserApi,
} from "../../../../services/ipWhitelisting.services";

const ipv4Octet = "(?:25[0-5]|2[0-4]\\d|1?\\d?\\d)";
const ipv4WithOptionalCidrRegex = new RegExp(
  `^${ipv4Octet}\\.${ipv4Octet}\\.${ipv4Octet}\\.${ipv4Octet}(?:\\/(\\d{1,2}))?$`
);

const isValidIpAddress = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return false;
  const match = trimmed.match(ipv4WithOptionalCidrRegex);
  if (!match) return false;
  if (match[1] !== undefined) {
    const prefix = Number(match[1]);
    return prefix >= 0 && prefix <= 32;
  }
  return true;
};

const resolveBypassUserId = (row) => {
  if (!row) return null;
  const nested = row?.user;
  if (nested != null && typeof nested === "object") {
    return nested?.id ?? nested?.user__id ?? nested?.user_id ?? null;
  }
  return row?.user__id ?? row?.user_id ?? row?.user ?? null;
};

const displayUserLabel = (userField) => {
  if (userField == null || userField === "") return "-";
  if (typeof userField === "string") {
    const t = userField.trim();
    return t || "-";
  }
  if (typeof userField === "object" && !Array.isArray(userField)) {
    const fromParts = [userField.first_name, userField.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    const label =
      userField.full_name ||
      fromParts ||
      userField.display_name ||
      userField.email ||
      (userField.mobile != null ? String(userField.mobile) : "");
    if (label) return label;
    if (userField.id != null) return `User #${userField.id}`;
    return "-";
  }
  return "-";
};

const modeMeta = (enabled, mode) => {
  const m = (mode || "").toLowerCase();
  if (!enabled) {
    return {
      title: "IP Whitelisting Disabled",
      desc: "All access is allowed from any IP.",
    };
  }
  if (m === "strict" || m === "strict_blocked") {
    return {
      title: "Strict Mode",
      desc: "Blocks all requests from IPs that are not explicitly whitelisted and logs each violation.",
    };
  }
  return {
    title: "Permissive Mode",
    desc: "Allow access from any IP, log violations for monitoring",
  };
};

const normalizeList = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (data?.data && typeof data.data === "object") return [data.data];
  if (typeof data === "object") return [data];
  return [];
};

const FacilityIpWhitelisting = () => {
  const { token } = theme.useToken();
  const { userDetails } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search)),
    [location.search]
  );

  const facilityId = useMemo(() => {
    const fromQuery = params?.facility;
    const fromCookie = getCookie(FacilityID);
    const fromUser =
      userDetails?.activeOrg?.facilities?.[0]?.facility_id ||
      userDetails?.currFacility;
    const raw = fromQuery || fromCookie || fromUser;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [params?.facility, userDetails]);
  const orgId = Number(getCookie(ORGANISATONID));

  const activeTopTab = params?.tab || "config";
  const activeConfigTab = params?.config_tab || "ips";
  const logsSearch = params?.search || "";
  const logsModeSort = params?.logs_sort || "none";

  const [config, setConfig] = useState(null);
  const [ips, setIps] = useState([]);
  const [bypassUsers, setBypassUsers] = useState([]);
  const [logs, setLogs] = useState([]);

  const [loading, setLoading] = useState(false);

  const [ipModalOpen, setIpModalOpen] = useState(false);
  const [ipEditing, setIpEditing] = useState(null);
  const [ipNotesExpanded, setIpNotesExpanded] = useState(false);
  const [ipForm] = Form.useForm();

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userForm] = Form.useForm();
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchText, setUserSearchText] = useState("");
  const [userHasSearched, setUserHasSearched] = useState(false);
  const [selectedBypassUser, setSelectedBypassUser] = useState(null);

  const { title: modeTitle, desc: modeDesc } = modeMeta(
    !!config?.enabled,
    config?.mode
  );

  const loadConfig = async () => {
    const res = await getIpWhitelistingConfigsApi();
    const raw = res?.data?.data ?? res?.data;
    const list = normalizeList(raw);
    const match =
      list.find((c) => String(c?.facility_id) === String(facilityId)) ||
      (raw && raw?.facility_id ? raw : null);
    setConfig(match);
  };

  const loadIps = async () => {
    const res = await getIpWhitelistingFacilityIpsApi(facilityId);
    setIps(normalizeList(res?.data));
  };

  const loadBypass = async () => {
    const res = await getIpWhitelistingBypassUsersApi();
    const list = normalizeList(res?.data);
    setBypassUsers(
      list.filter(
        (x) =>
          String(x?.facility_id ?? x?.facility ?? x?.facility?.id) ===
          String(facilityId)
      )
    );
  };

  const loadLogs = async () => {
    const qs = new URLSearchParams({
      facility_id: String(facilityId),
      limit: "100",
    }).toString();

    const res = await getIpWhitelistingAccessLogsApi(qs);
    const list = normalizeList(res?.data);
    setLogs(list);
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await loadConfig();
      await Promise.all([loadIps(), loadBypass()]);
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.detail ||
          "Failed to load IP whitelisting data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!facilityId) {
      message.error("Unable to determine facility for IP Whitelisting");
      return;
    }
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  useEffect(() => {
    if (activeTopTab !== "logs") return;
    if (!facilityId) return;
    loadLogs().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopTab, facilityId, config?.enabled, config?.mode]);

  const openAddIp = () => {
    setIpEditing(null);
    ipForm.resetFields();
    setIpModalOpen(true);
  };

  const openEditIp = (row) => {
    setIpEditing(row);
    ipForm.resetFields();
    ipForm.setFieldsValue({
      label: row?.label,
      value: row?.value,
    });
    setIpModalOpen(true);
  };

  const saveIp = async (values) => {
    try {
      if (ipEditing?.id) {
        await updateIpWhitelistingFacilityIpApi(ipEditing.id, {
          label: values.label,
          value: values.value,
        });
      } else {
        await addIpWhitelistingFacilityIpApi(facilityId, {
          organization: orgId,
          facility: facilityId,
          label: values.label,
          value: values.value,
        });
      }
      message.success(
        ipEditing?.id ? "IP Address updated" : "IP Address saved"
      );
      setIpModalOpen(false);
      setIpEditing(null);
      await loadIps();
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.detail ||
          "Failed to save IP address"
      );
    }
  };

  const deleteIp = async (row) => {
    try {
      await deleteIpWhitelistingFacilityIpApi(row?.id);
      message.success("IP Address deleted");
      await loadIps();
    } catch (e) {
      message.error("Failed to delete IP address");
    }
  };

  const searchUsers = useCallback(
    async (q) => {
      const term = (q || "").trim();
      if (!term) {
        setUserOptions([]);
        setUserHasSearched(false);
        return;
      }
      setUserHasSearched(true);
      if (!facilityId) {
        message.error("Missing facility id for user search");
        setUserOptions([]);
        return;
      }
      setUserSearchLoading(true);
      try {
        // Same API + params as Receptionist -> Appointments -> PractitionerSearch
        const res = await getPractitionerSearchApi(
          `query=${encodeURIComponent(
            term
          )}&facility=${facilityId}&role=practitioners&include=Receptionist`
        );
        const raw = res?.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.results)
          ? raw.results
          : Array.isArray(raw?.data)
          ? raw.data
          : normalizeList(raw);

        const existingBypassIds = new Set(
          (bypassUsers || [])
            .map((b) => resolveBypassUserId(b))
            .filter((id) => id != null)
            .map(String)
        );

        const opts = (list || [])
          .map((u) => {
            // Backend expects user__id for bypass create payload
            const userInternalId =
              u?.user__id ?? u?.user_id ?? u?.id ?? u?.org_user_id;
            if (!userInternalId) return null;
            if (existingBypassIds.has(String(userInternalId))) return null;
            return {
              value: `${capitalizeFirstLetter(
                u?.first_name || ""
              )} ${capitalizeFirstLetter(
                u?.last_name || ""
              )} (${capitalizeFirstLetter(u?.role || "")})`.trim(),
              key: userInternalId,
              label: (
                <span>
                  {capitalizeFirstLetter(u?.first_name || "")}{" "}
                  {capitalizeFirstLetter(u?.last_name || "")}{" "}
                  <span style={{ color: "#475467" }}>({u?.role})</span>
                </span>
              ),
            };
          })
          .filter(Boolean);

        setUserOptions(opts);
      } catch (e) {
        setUserOptions([]);
        message.error(
          e?.response?.data?.message ||
            e?.response?.data?.detail ||
            "Failed to search users"
        );
      } finally {
        setUserSearchLoading(false);
      }
    },
    [facilityId, bypassUsers]
  );

  const debouncedSearchUsers = useMemo(
    () => debounce(searchUsers, 300),
    [searchUsers]
  );

  const isBypassUserAlreadyAdded = (userId) => {
    if (userId == null) return false;
    return (bypassUsers || []).some(
      (row) => String(resolveBypassUserId(row)) === String(userId)
    );
  };

  const addBypassUser = async (values) => {
    const userId = values.user_id;
    if (!userId) {
      message.error("Please select a user");
      return;
    }
    if (isBypassUserAlreadyAdded(userId)) {
      message.error("User already added");
      return;
    }
    try {
      await createIpWhitelistingBypassUserApi({
        user_id: userId,
        facility_id: facilityId,
        is_active: true,
      });
      message.success("User added");
      setUserModalOpen(false);
      userForm.resetFields();
      setUserSearchText("");
      setUserOptions([]);
      setUserHasSearched(false);
      setSelectedBypassUser(null);
      await loadBypass();
    } catch (e) {
      const apiMsg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.response?.data?.user_id?.[0];
      const msg = String(apiMsg || "").toLowerCase();
      if (msg.includes("already") || msg.includes("exist")) {
        message.error("User already added");
      } else {
        message.error(apiMsg || "Failed to add user");
      }
    }
  };

  const openAddUser = () => {
    userForm.resetFields();
    setUserSearchText("");
    setUserOptions([]);
    setUserHasSearched(false);
    setSelectedBypassUser(null);
    setUserModalOpen(true);
  };

  const toggleBypass = async (row, checked) => {
    try {
      await updateIpWhitelistingBypassUserApi(row?.id, {
        facility: facilityId,
        user: row?.user__id ?? row?.user ?? row?.user_id ?? row?.user?.id,
        is_active: checked,
      });
      await loadBypass();
    } catch (e) {
      message.error("Failed to update bypass");
    }
  };

  const deleteBypass = async (row) => {
    try {
      await deleteIpWhitelistingBypassUserApi(row?.id);
      message.success("User removed");
      await loadBypass();
    } catch (e) {
      message.error("Failed to remove user");
    }
  };

  const renderIpWhitelistingNotes = () => (
    <div
      className={styles.ipNotesBanner}
      style={{
        background: token.colorPrimaryBg,
        borderColor: `color-mix(in srgb, ${token.colorPrimary} 22%, white)`,
      }}
    >
      <InfoCircleOutlined
        className={styles.ipNotesIcon}
        style={{ color: token.colorPrimary }}
      />
      <div className={styles.ipNotesContent}>
        <button
          type="button"
          className={styles.ipNotesHeader}
          onClick={() => setIpNotesExpanded((prev) => !prev)}
          style={{ color: token.colorPrimary }}
        >
          <span>Facing login issues even after IP whitelisting?</span>
          {ipNotesExpanded ? (
            <CaretUpOutlined style={{ fontSize: 12 }} />
          ) : (
            <CaretDownOutlined style={{ fontSize: 12 }} />
          )}
        </button>
        {ipNotesExpanded && (
          <div className={styles.ipNotesBody}>
            <Typography.Paragraph style={{ marginBottom: 12 }}>
              Some office or ISP networks may use multiple public IP addresses.
              Because of this, the IP address detected by the application may
              slightly differ from the one shown in browser IP-check tools. To
              avoid access issues, IP access may be configured using a small
              network range instead of a single IP address.
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 8 }}>
              <strong>Example:</strong>
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 8 }}>
              Instead of allowing only <strong>XXX.XXX.XXX.10</strong>, a
              network range such as <strong>XXX.XXX.XXX.10/31</strong> can be
              used, which covers:
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 4, paddingLeft: 16 }}>
              <strong>XXX.XXX.XXX.10</strong>
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0, paddingLeft: 16 }}>
              <strong>XXX.XXX.XXX.11</strong>
            </Typography.Paragraph>
          </div>
        )}
      </div>
    </div>
  );

  const renderIps = () => {
    if (!ips || ips.length === 0) {
      return (
        <>
          {renderIpWhitelistingNotes()}
          <div className={styles.emptyCard}>
            <div className={styles.emptyInner}>
              <div style={{ marginBottom: 10 }}>
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 12C0 5.37258 5.37258 0 12 0H44C50.6274 0 56 5.37258 56 12V44C56 50.6274 50.6274 56 44 56H12C5.37258 56 0 50.6274 0 44V12Z"
                    fill={token.colorPrimaryBg}
                  />
                  <path
                    d="M33 36H32.8C31.1198 36 30.2798 36 29.638 35.673C29.0735 35.3854 28.6146 34.9265 28.327 34.362C28 33.7202 28 32.8802 28 31.2V24.8C28 23.1198 28 22.2798 28.327 21.638C28.6146 21.0735 29.0735 20.6146 29.638 20.327C30.2798 20 31.1198 20 32.8 20H33M33 36C33 37.1046 33.8954 38 35 38C36.1046 38 37 37.1046 37 36C37 34.8954 36.1046 34 35 34C33.8954 34 33 34.8954 33 36ZM33 20C33 21.1046 33.8954 22 35 22C36.1046 22 37 21.1046 37 20C37 18.8954 36.1046 18 35 18C33.8954 18 33 18.8954 33 20ZM23 28L33 28M23 28C23 29.1046 22.1046 30 21 30C19.8954 30 19 29.1046 19 28C19 26.8954 19.8954 26 21 26C22.1046 26 23 26.8954 23 28ZM33 28C33 29.1046 33.8954 30 35 30C36.1046 30 37 29.1046 37 28C37 26.8954 36.1046 26 35 26C33.8954 26 33 26.8954 33 28Z"
                    stroke={token.colorPrimary}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className={styles.emptyTitle}>No IP Address added yet</div>
              <div className={styles.emptySubtitle}>
                Get started by adding your first IP Address
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  background: token.colorPrimary,
                  borderColor: token.colorPrimary,
                }}
                onClick={openAddIp}
              >
                Add IP Address
              </Button>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        {renderIpWhitelistingNotes()}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>IP Address List</div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{
                background: token.colorPrimary,
                borderColor: token.colorPrimary,
              }}
              onClick={openAddIp}
            >
              Add IP Address
            </Button>
          </div>

          <div className={styles.tableHeader}>
            <div>Label</div>
            <div>IP Address</div>
            <div style={{ textAlign: "right" }}>Action</div>
          </div>

          {ips.map((row) => (
            <div
              key={row?.id ?? `${row?.label}-${row?.value}`}
              className={styles.row}
            >
              <div>{row?.label || "-"}</div>
              <div>
                <span className={styles.ipPill}>{row?.value || "-"}</span>
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.actionIcon}
                  onClick={() => openEditIp(row)}
                >
                  <EditIcon />
                </button>
                <button
                  className={styles.actionIcon}
                  onClick={() => deleteIp(row)}
                >
                  <DeleteOutlined />
                </button>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderBypass = () => {
    if (!bypassUsers || bypassUsers.length === 0) {
      return (
        <div className={styles.emptyCard}>
          <div className={styles.emptyInner}>
            <div style={{ marginBottom: 10 }}>
              <svg
                width="56"
                height="56"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 12C0 5.37258 5.37258 0 12 0H44C50.6274 0 56 5.37258 56 12V44C56 50.6274 50.6274 56 44 56H12C5.37258 56 0 50.6274 0 44V12Z"
                  fill={token.colorPrimaryBg}
                />
                <path
                  d="M36 37C36 35.6044 36 34.9067 35.8278 34.3389C35.44 33.0605 34.4395 32.06 33.1611 31.6722C32.5933 31.5 31.8956 31.5 30.5 31.5H25.5C24.1044 31.5 23.4067 31.5 22.8389 31.6722C21.5605 32.06 20.56 33.0605 20.1722 34.3389C20 34.9067 20 35.6044 20 37M32.5 23.5C32.5 25.9853 30.4853 28 28 28C25.5147 28 23.5 25.9853 23.5 23.5C23.5 21.0147 25.5147 19 28 19C30.4853 19 32.5 21.0147 32.5 23.5Z"
                  stroke={token.colorPrimary}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={styles.emptyTitle}>No Users added yet</div>
            <div className={styles.emptySubtitle}>
              Get started by adding users
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{
                background: token.colorPrimary,
                borderColor: token.colorPrimary,
              }}
              onClick={openAddUser}
            >
              Add User
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.cardTitle}>User List</div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{
              background: token.colorPrimary,
              borderColor: token.colorPrimary,
            }}
            onClick={openAddUser}
          >
            Add User
          </Button>
        </div>

        <div
          className={styles.tableHeader}
          style={{ gridTemplateColumns: "2fr 1fr 120px" }}
        >
          <div>User Name</div>
          <div style={{ textAlign: "center" }}>Bypass Enabled</div>
          <div style={{ textAlign: "right" }}>Action</div>
        </div>

        {bypassUsers.map((row) => {
          const userName = displayUserLabel(
            row?.full_name ?? row?.user ?? row?.user_detail
          );
          return (
            <div
              key={row?.id}
              className={styles.row}
              style={{ gridTemplateColumns: "2fr 1fr 120px" }}
            >
              <div>{userName}</div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Switch
                  checked={!!row?.is_active}
                  onChange={(c) => toggleBypass(row, c)}
                />
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.actionIcon}
                  onClick={() => deleteBypass(row)}
                >
                  <DeleteOutlined />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLogs = () => {
    const header = ["Username", "IP Address", "Mode", "Blocked/Accessed at"];
    const normalizedSearch = (logsSearch || "").trim().toLowerCase();

    const filteredLogs = (logs || []).filter((row) => {
      const rawOutcome = String(row?.last_outcome ?? row?.outcome ?? "")
        .toLowerCase()
        .trim();
      const outcomeGroup = rawOutcome.startsWith("strict")
        ? "strict"
        : rawOutcome === "permissive"
        ? "permissive"
        : rawOutcome || "unknown";

      if (!normalizedSearch) return true;

      const userName = displayUserLabel(
        row?.user_detail ?? row?.user ?? row?.sent_by
      );
      const ip = String(row?.client_ip || "");

      return (
        userName.toLowerCase().includes(normalizedSearch) ||
        ip.toLowerCase().includes(normalizedSearch)
      );
    });

    const sortedLogs = (() => {
      const arr = [...filteredLogs];
      if (logsModeSort === "none") return arr;

      const rank = (row) => {
        const rawOutcome = String(row?.last_outcome ?? row?.outcome ?? "")
          .toLowerCase()
          .trim();
        if (rawOutcome.startsWith("strict")) return 2;
        if (rawOutcome === "permissive") return 1;
        return 3;
      };

      arr.sort((a, b) => {
        const da = rank(a);
        const db = rank(b);
        if (da === db) return 0;
        return logsModeSort === "asc" ? da - db : db - da;
      });
      return arr;
    })();

    if (!logs || logs.length === 0) {
      return (
        <div className={styles.emptyCard}>
          <div className={styles.emptyInner}>
            <div style={{ marginBottom: 10 }}>
              <svg
                width="56"
                height="56"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 12C0 5.37258 5.37258 0 12 0H44C50.6274 0 56 5.37258 56 12V44C56 50.6274 50.6274 56 44 56H12C5.37258 56 0 50.6274 0 44V12Z"
                  fill={token.colorPrimaryBg}
                />
                {/* file-x-02.svg centered in the 56x56 container */}
                <g transform="translate(16 16)">
                  <path
                    d="M14 11H8M10 15H8M16 7H8M20 12V6.8C20 5.11984 20 4.27976 19.673 3.63803C19.3854 3.07354 18.9265 2.6146 18.362 2.32698C17.7202 2 16.8802 2 15.2 2H8.8C7.11984 2 6.27976 2 5.63803 2.32698C5.07354 2.6146 4.6146 3.07354 4.32698 3.63803C4 4.27976 4 5.11984 4 6.8V17.2C4 18.8802 4 19.7202 4.32698 20.362C4.6146 20.9265 5.07354 21.3854 5.63803 21.673C6.27976 22 7.11984 22 8.8 22H12"
                    stroke={token.colorPrimary}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <g clipPath="url(#clip0_file_x_02)">
                    <path
                      d="M19.0002 17.3334V19M19.0002 20.6667H19.0043M23.1668 19C23.1668 21.3012 21.3013 23.1667 19.0002 23.1667C16.699 23.1667 14.8335 21.3012 14.8335 19C14.8335 16.6989 16.699 14.8334 19.0002 14.8334C21.3013 14.8334 23.1668 16.6989 23.1668 19Z"
                      stroke={token.colorPrimary}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_file_x_02">
                      <rect
                        width="10"
                        height="10"
                        fill="white"
                        transform="translate(14 14)"
                      />
                    </clipPath>
                  </defs>
                </g>
              </svg>
            </div>
            <div className={styles.emptyTitle}>No logs yet</div>
            <div className={styles.emptySubtitle}>
              Logs will appear here once available
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.card}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            padding: "12px 16px 12px",
            flexWrap: "wrap",
          }}
        >
          <Input
            allowClear
            placeholder="Search"
            value={logsSearch}
            onChange={(e) =>
              updateUrlSearchParams(navigate, { search: e.target.value || null })
            }
            prefix={<SearchOutlined style={{ color: "#98A2B3" }} />}
            style={{ width: 260 }}
          />
        </div>

        <div
          className={styles.tableHeader}
          style={{
            gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr",
          }}
        >
          <div>{header[0]}</div>
          <div>{header[1]}</div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              const next =
                logsModeSort === "none"
                  ? "asc"
                  : logsModeSort === "asc"
                    ? "desc"
                    : "none";
              updateUrlSearchParams(navigate, {
                logs_sort: next === "none" ? null : next,
              });
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              const next =
                logsModeSort === "none"
                  ? "asc"
                  : logsModeSort === "asc"
                    ? "desc"
                    : "none";
              updateUrlSearchParams(navigate, {
                logs_sort: next === "none" ? null : next,
              });
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              userSelect: "none",
              justifyContent: "center",
            }}
          >
            <span>{header[2]}</span>
            <span
              style={{
                display: "inline-flex",
                flexDirection: "column",
                lineHeight: 1,
                marginTop: 1,
              }}
            >
              <CaretUpOutlined
                style={{
                  fontSize: 10,
                  color: logsModeSort === "asc" ? "#344054" : "#98A2B3",
                  marginBottom: -2,
                }}
              />
              <CaretDownOutlined
                style={{
                  fontSize: 10,
                  color: logsModeSort === "desc" ? "#344054" : "#98A2B3",
                }}
              />
            </span>
          </div>
          <div>{header[3]}</div>
        </div>

        {sortedLogs.map((row) => {
          const userName = displayUserLabel(
            row?.user_detail ?? row?.user ?? row?.sent_by
          );
          const ip = row?.client_ip || "-";
          const when = row?.last_seen_at;
          const rawOutcome = String(row?.last_outcome ?? row?.outcome ?? "-")
            .toLowerCase()
            .trim();
          const outcomeGroup = rawOutcome.startsWith("strict")
            ? "strict"
            : rawOutcome === "permissive"
            ? "permissive"
            : "unknown";
          const isPermissive = outcomeGroup === "permissive";
          const modeLabel =
            outcomeGroup === "strict"
              ? "Strict"
              : outcomeGroup === "permissive"
              ? "Permissive"
              : capitalizeFirstLetter(rawOutcome) || "-";
          const modeColor = isPermissive ? "#067647" : "#B42318";
          const modeBorder = isPermissive ? "#ABEFC6" : "#FDA29B";
          const modeBg = isPermissive ? "#ECFDF3" : "#FEF3F2";
          return (
            <div
              key={row?.id}
              className={styles.row}
              style={{
                gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr",
              }}
            >
              <div>{userName || "-"}</div>
              <div>
                <span className={styles.ipPill}>{ip}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "2px 10px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 500,
                    border: `1px solid ${modeBorder}`,
                    background: modeBg,
                    color: modeColor,
                    textTransform: "capitalize",
                    lineHeight: "20px",
                  }}
                >
                  {modeLabel}
                </span>
              </div>
              <div
                style={{
                  color: outcomeGroup === "unknown" ? undefined : modeColor,
                }}
              >
                {when ? new Date(when).toLocaleString() : "-"}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const body = (
    <div className={styles.wrap}>
      {activeTopTab === "config" && (
        <>
          <div className={styles.modeTitle}>{modeTitle}</div>
          <div className={styles.modeDesc}>{modeDesc}</div>
        </>
      )}

      {activeTopTab === "config" ? (
        <ConfigProvider
          theme={{
            components: {
              Tabs: {
                colorBorderSecondary: "transparent",
              },
            },
          }}
        >
          <Tabs
            className={styles.segPills}
            activeKey={activeConfigTab}
            onChange={(key) => updateUrlSearchParams(navigate, { config_tab: key })}
            items={[
              { key: "ips", label: "IP Whitelisting", children: renderIps() },
              { key: "bypass", label: "Bypass User", children: renderBypass() },
            ]}
          />
        </ConfigProvider>
      ) : (
        renderLogs()
      )}

      <Modal
        open={ipModalOpen}
        centered
        footer={null}
        onCancel={() => {
          setIpModalOpen(false);
          setIpEditing(null);
        }}
        title={<div className={styles.modalTitle}>Add IP Address</div>}
      >
        <Form
          form={ipForm}
          layout="vertical"
          onFinish={saveIp}
          initialValues={{ label: "", value: "" }}
        >
          <Form.Item
            name="label"
            label="Label"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Please enter label",
              },
              {
                max: 200,
                message: "Label exceeds maximum limit of 200 characters",
              },
            ]}
          >
            <Input placeholder="e.g. 1st Floor" />
          </Form.Item>
          <Form.Item
            name="value"
            label="Enter IP Address"
            rules={[
              { required: true, message: "Please enter IP address" },
              {
                validator: (_, v) =>
                  !v || isValidIpAddress(v)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Enter a valid IPv4 address ")),
              },
            ]}
          >
            <Input placeholder="e.g. 192.168.1.0 or 192.168.1.0/24" />
          </Form.Item>

          <div className={styles.modalFooter}>
            <Button
              onClick={() => {
                setIpModalOpen(false);
                setIpEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                background: token.colorPrimary,
                borderColor: token.colorPrimary,
              }}
            >
              Save Address
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={userModalOpen}
        centered
        footer={null}
        onCancel={() => {
          setUserModalOpen(false);
          userForm.resetFields();
          setUserSearchText("");
          setUserOptions([]);
          setUserHasSearched(false);
          setSelectedBypassUser(null);
        }}
        title={<div className={styles.modalTitle}>Add User</div>}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={addBypassUser}
          onFinishFailed={() => message.error("Please select a user")}
        >
          <Form.Item
            name="user_id"
            hidden
            rules={[{ required: true, message: "Please select a user" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Search User" required>
            <AutoComplete
              options={userOptions}
              className="search"
              onSearch={debouncedSearchUsers}
              onSelect={(value, option) => {
                const selection = { key: option?.key, value };
                setSelectedBypassUser(selection);
                setUserSearchText(value);
                userForm.setFieldsValue({ user_id: option?.key });
              }}
              onChange={(value) => {
                setUserSearchText(value);
                if (!String(value || "").trim()) {
                  setSelectedBypassUser(null);
                  userForm.setFieldsValue({ user_id: undefined });
                }
                debouncedSearchUsers(value);
              }}
              value={userSearchText}
              style={{ width: "100%" }}
              notFoundContent={
                userHasSearched
                  ? userSearchLoading
                    ? "Searching..."
                    : "No results"
                  : null
              }
            >
              <Input
                size="large"
                prefix={<SearchOutlined />}
                placeholder="Search User"
                className={inputStyles.mdInput}
                style={{ width: "100%" }}
              />
            </AutoComplete>
          </Form.Item>

          <div className={styles.modalFooter}>
            <Button
              onClick={() => {
                setUserModalOpen(false);
                userForm.resetFields();
                setUserSearchText("");
                setUserOptions([]);
                setUserHasSearched(false);
                setSelectedBypassUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                background: token.colorPrimary,
                borderColor: token.colorPrimary,
              }}
            >
              Add
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
      <Typography style={{ fontSize: 18, fontWeight: 700 }}>
        IP Whitelisting
      </Typography>
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              itemSelectedColor: token.colorPrimary,
              inkBarColor: token.colorPrimary,
            },
          },
        }}
      >
        <Tabs
          className={styles.topTabs}
          activeKey={activeTopTab}
          onChange={(key) => updateUrlSearchParams(navigate, { tab: key })}
          items={[
            { key: "config", label: "Configurations" },
            { key: "logs", label: "Logs" },
          ]}
        />
      </ConfigProvider>
    </div>
  );

  return (
    <CustomLayout header={header} sideContent={<SideMenu />}>
      {body}
    </CustomLayout>
  );
};

export default FacilityIpWhitelisting;
